import React, { useEffect, useState } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Drawer, List, Divider, ListSubheader, Hidden } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import {
	EventNote,
	ExpandLess,
	ExpandMore,
	Sports,
	SportsBasketball,
	SportsCricket,
	SportsFootball,
	SportsGolf,
	SportsMotorsports,
	SportsSoccer,
	Tv
} from '@material-ui/icons';
import { useIntl } from 'react-intl';
import { useFeatures } from 'flagged';
import { ListItemLink, ListItemATagLink } from '../ListItemLink';
import { AuthLink } from '../Auth';
import messages from './messages';

import './sidebar.css';
import { fetchAppConfigByProfileName } from '../Appconfig';
import { DailyTestGame } from '../../../utils/types';
import awsConfig from '../../../aws-exports';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		drawer: ({ width }: StyleProps) => ({
			[theme.breakpoints.up('lg')]: {
				width,
				flexShrink: 0
			}
		}),
		drawerPaper: ({ width }: StyleProps) => ({
			width
		}),
		drawerHeader: {
			display: 'flex',
			alignItems: 'center',
			padding: theme.spacing(0, 1),
			// necessary for content to be below app bar
			...theme.mixins.toolbar,
			justifyContent: 'flex-end'
		},
		logo: {
			marginLeft: theme.spacing(1)
		},
		grow: {
			flexGrow: 1
		},
		dailyTestingIcon: {
			position: 'relative',
			top: 7
		},
		dailyTestingExpandIcon: {
			position: 'absolute',
			top: 7,
			left: 160
		},
		scheduleExpandIcon: {
			position: 'absolute',
			top: 11,
			left: 160
		}
	})
);

const Sidebar: React.FC<SidebarProps> = ({ width, mobileOpen, onCloseMobileSidebar }) => {
	const [footballOpen, setFootballOpen] = useState(false);
	const [golfOpen, setGolfOpen] = useState(false);
	const [f1Open, setF1Open] = useState(false);
	const [cricketOpen, setCricketOpen] = useState(false);
	const [amFootballOpen, setAmFootballOpen] = useState(false);
	const [basketballOpen, setBasketballOpen] = useState(false);

	const [scheduleOpen, setScheduleOpen] = useState(false);

	const classes = useStyles({ width });
	const intl = useIntl();
	const {
		dailyTestingFootball,
		dailyTestingGolf,
		dailyTestingF1,
		dailyTestingCricket,
		dailyTestingAmericanFootball,
		dailyTestingBasketball,
		ukFootballRecapSchedule,
		germanFootballRecapSchedule,
		italianFootballRecapSchedule,
		f1RecapSchedule,
		cricketRecapSchedule,
		peacockLemFeedSchedule
	} = useFeatures();

	useEffect(() => {
		const getAppConfigs = async (): Promise<void> => {
			const mgmtUiAppconfigConfigsProfile = await fetchAppConfigByProfileName('mgmt_ui_appconfig_configs_profile');
			const dailyTestGameConfigsProfile = await fetchAppConfigByProfileName('daily-test-game-configs');
			if (!mgmtUiAppconfigConfigsProfile || !dailyTestGameConfigsProfile) {
				return;
			}
			const mgmtUiConfigs = JSON.parse(mgmtUiAppconfigConfigsProfile);
			const dailyTestGames = JSON.parse(dailyTestGameConfigsProfile);
			if (mgmtUiConfigs.scheduleLinks) setScheduleLinks(mgmtUiConfigs.scheduleLinks);
			setRecapDailyTestGames(dailyTestGames[awsConfig.accountName] ?? dailyTestGames['development'] ?? {});
		};
		getAppConfigs();
	}, []);
	const [scheduleLinks, setScheduleLinks] = useState<Record<string, string>>({});
	const [recapDailyTestGames, setRecapDailyTestGames] = useState<Record<string, DailyTestGame[]>>({});

	const getDailyTestingGames = (sport: string, recapDailyGames: DailyTestGame[], icon: JSX.Element, open: boolean, setOpen): JSX.Element => {
		const games = recapDailyGames.map((eachGame) => {
			return {
				title: eachGame.abbrTitle ?? eachGame.gameObject.title,
				link: `/games/${eachGame.gameObject.id}`
			};
		});
		return (
			<List
				subheader={
					<ListSubheader
						component='div'
						onClick={(): void => {
							setOpen(!open);
						}}>
						<span className={classes.dailyTestingIcon}>{icon}</span> {sport}
						<span className={classes.dailyTestingExpandIcon}> {open ? <ExpandLess /> : <ExpandMore />}</span>
					</ListSubheader>
				}>
				<Collapse in={open} timeout={200} unmountOnExit>
					{games.map(({ link, title }, idx) => (
						<ListItemLink to={link} label={title} key={`link_${idx}`} />
					))}
				</Collapse>
			</List>
		);
	};

	const drawerChildren: JSX.Element = (
		<>
			<div className={classes.drawerHeader}>
				<div className={`${classes.logo} sidebar__logo`} />
				<div className={classes.grow} />
			</div>
			<Divider />
			<List>
				<ListItemLink
					to='/channels'
					label={intl.formatMessage({ ...messages.channels })}
					icon={<Tv style={{ marginRight: '0.5rem' }} />}
					testid='sidebar-items-channels'
				/>
				<ListItemLink
					to='/games'
					label={intl.formatMessage({ ...messages.games })}
					icon={<Sports style={{ marginRight: '0.5rem' }} />}
					testid='sidebar-items-games'
				/>
			</List>
			<Divider />
			<ListSubheader component='div'>{intl.formatMessage({ ...messages.dailyTests })}</ListSubheader>
			{dailyTestingFootball && recapDailyTestGames['football'] && recapDailyTestGames['football'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.football }),
					recapDailyTestGames['football'],
					<SportsSoccer style={{ marginRight: '0.5rem' }} />,
					footballOpen,
					setFootballOpen
				)
			) : (
				<></>
			)}
			{dailyTestingGolf && recapDailyTestGames['golf'] && recapDailyTestGames['golf'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.golf }),
					recapDailyTestGames['golf'],
					<SportsGolf style={{ marginRight: '0.5rem' }} />,
					golfOpen,
					setGolfOpen
				)
			) : (
				<></>
			)}
			{dailyTestingF1 && recapDailyTestGames['f1'] && recapDailyTestGames['f1'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.f1 }),
					recapDailyTestGames['f1'],
					<SportsMotorsports style={{ marginRight: '0.5rem' }} />,
					f1Open,
					setF1Open
				)
			) : (
				<></>
			)}
			{dailyTestingCricket && recapDailyTestGames['cricket'] && recapDailyTestGames['cricket'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.cricket }),
					recapDailyTestGames['cricket'],
					<SportsCricket style={{ marginRight: '0.5rem' }} />,
					cricketOpen,
					setCricketOpen
				)
			) : (
				<></>
			)}
			{dailyTestingAmericanFootball && recapDailyTestGames['americanfootball'] && recapDailyTestGames['americanfootball'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.americanfootball }),
					recapDailyTestGames['americanfootball'],
					<SportsFootball style={{ marginRight: '0.5rem' }} />,
					amFootballOpen,
					setAmFootballOpen
				)
			) : (
				<></>
			)}
			{dailyTestingBasketball && recapDailyTestGames['basketball'] && recapDailyTestGames['basketball'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.basketball }),
					recapDailyTestGames['basketball'],
					<SportsBasketball style={{ marginRight: '0.5rem' }} />,
					basketballOpen,
					setBasketballOpen
				)
			) : (
				<></>
			)}
			<Divider />

			<List
				onClick={(): void => {
					setScheduleOpen(!scheduleOpen);
				}}
				subheader={
					<ListSubheader component='div'>
						{intl.formatMessage({ ...messages.recapSchedule })}{' '}
						<span className={classes.dailyTestingExpandIcon}> {scheduleOpen ? <ExpandLess /> : <ExpandMore />}</span>
					</ListSubheader>
				}>
				<Collapse in={scheduleOpen} timeout={200} unmountOnExit>
					{ukFootballRecapSchedule && (
						<ListItemATagLink
							href={scheduleLinks.ukFootballRecapScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.ukFootballSchedule })}
							icon={<EventNote style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{germanFootballRecapSchedule && (
						<ListItemATagLink
							href={scheduleLinks.germanFootballRecapScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.germanFootballSchedule })}
							icon={<EventNote style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{italianFootballRecapSchedule && (
						<ListItemATagLink
							href={scheduleLinks.italianFootballRecapScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.italianFootballSchedule })}
							icon={<EventNote style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{f1RecapSchedule && (
						<ListItemATagLink
							href={scheduleLinks.f1RecapScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.f1Schedule })}
							icon={<EventNote style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{cricketRecapSchedule && (
						<ListItemATagLink
							href={scheduleLinks.cricketRecapScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.cricketSchedule })}
							icon={<EventNote style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{peacockLemFeedSchedule && (
						<ListItemATagLink
							href={scheduleLinks.peacockLemFeedScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.peacockLemFeed })}
							icon={<EventNote style={{ marginRight: '0.5rem' }} />}
						/>
					)}
				</Collapse>
			</List>

			<Divider />
			<AuthLink />
		</>
	);

	return (
		<nav className={classes.drawer}>
			<Hidden smUp implementation='css'>
				<Drawer
					data-testid='sidebar-mobile'
					variant='temporary'
					anchor='left'
					classes={{
						paper: classes.drawerPaper
					}}
					open={mobileOpen}
					onClose={onCloseMobileSidebar}
					ModalProps={{
						keepMounted: true // Better open performance on mobile.
					}}>
					<div onClick={onCloseMobileSidebar}>{drawerChildren}</div>
				</Drawer>
			</Hidden>
			<Hidden mdDown implementation='css'>
				<Drawer
					variant='permanent'
					anchor='left'
					classes={{
						paper: classes.drawerPaper
					}}
					open>
					{drawerChildren}
				</Drawer>
			</Hidden>
		</nav>
	);
};

export default Sidebar;

type StyleProps = {
	width: number;
};

type SidebarProps = {
	width: number;
	mobileOpen: boolean;
	onCloseMobileSidebar: React.MouseEventHandler<HTMLButtonElement | HTMLDivElement>;
};
